import { changePlayer, isMe, offerEffect, pauseUpdates, sendUpdate, unPauseUpdates,  } from './net.js';
import { clone, res, prefs, state, ui } from './stores.js';
import { get } from 'svelte/store';
import { pause, choose, chooseCards, info, announce, cc, waitMillis, changeView, currentPlayer, advance, untilManualActionsDone } from './ui.js';
import { drawCard, myAway, theirAway, howManyOnDuty, clearCards, cardsInPlay, myOrbiting, theirOrbiting, canControlLocation, canGainControl, matches, resolveStardateCards, canActivate, canPlay, closeAllLooks, playerResourceToken, takeFromGloryPile, deactivateCard, faceupDraw, reshuffleDeck, possArray, drones, hasMurf, drawToHandSize, updateAllTypeGains } from './card_actions.js';
import { doAction, getPlayerIndex, moveTo, logText, shuffle, getMarketCards, log, updateState, handSize, closeWorkingLog, getNeutralZone, prng, openWorkingLog, allCards, starterActions, badlands, isEligibleForReaction, freeOrderActions, getActionsPerTurn, checkForReactions, resetCache, isBadlands, updateAllTransients, getP, markUndoTurnStart } from './game.js';
import { tick } from 'svelte/internal';
import { disableSave, enableSave, saveGame } from './save.js';
import { upperInitial, ongoingEffects, doActionPause, autoCardActions, getHints, ongoingPlayer, resolveAction, getCardEffect, meetsThreshold, resolveEffects, validEffect, resolveNum, otherPlayer, testNum, shipsAtLocation, ongoingPlayerWithCards } from './parse.js';
import { allScoreableCards, scoreGame, scorePlayer } from './scores.js';
import { howManyBotActions, countPlayerTokens, doBotAction, botValue, resolveBotCard, nextBotLevel, countSpecificTokens, botFavouriteCards, botLeastValuableCard } from './bot.js';
import { alreadyAddedCards, campaignRewards, eligibleRewards, getCampaignBotLevel, nextRank } from './campaign.js';
import { makeInvite, reactQueue, sendQueue, togglePublic, untilQueueEmpties } from './sb.js';
import { deleteItem } from './anydb.js';
import { playSound } from './sounds.js';
import { markContinuationCheckpoint, prepareContinuationReplay } from './continuation.js';
import { perfCount } from './perf.js';

// Resolves true when the condition passes, false if `timeout` ms elapse first.
// Callers that ignore the result keep working; anything on the sync path must
// pass a timeout and check it, because an untimed wait here parks whatever queue
// slot or turn loop is holding it, forever.
export function until(conditionFunction, timeout = false, interval = 400) {
    let started = Date.now();
    const poll = resolve => {
        awaitWrapper(conditionFunction).then((res) => {
            if(res) resolve(true);
            else if(timeout && Date.now() - started >= timeout) resolve(false);
            else setTimeout(_ => poll(resolve), interval);
        })
    }

    return new Promise(poll);
}

async function awaitWrapper(func){
    return await func();
}

function cleanupBackState(){
    let saved = get(ui).savedState;
    if(!saved || !saved.players){
        saved = get(res).savedState;
    }
    if(!saved || !saved.players){
        saved = get(state);
    }
    let restored = clone(saved);
    if(!Array.isArray(restored.log)) restored.log = [];
    if(!restored.choices){
        restored.choices = {
            thisTurn: [],
            all: [],
            num: 1
        };
    }
    restored.workingLog = false;
    return {
        ...restored,
        phase: 'action',
        step: 'playing'
    };
}

function endNow(){
    if(get(state).endNow) return true;
}

export function analyseTurnLengths(s){
    if(!s.players[0].stats || s.players[0].stats.turns || s.players[0].stats.turns[0] || s.players[0].stats.turns[0].timeAtStartOfTurn) return false;
    let lastTime = s.players[0].stats.turns[0].timeAtStartOfTurn;
    let turnCount = 1;
    let lengths = {};
    lengths[s.players[0].captain] = [];
    lengths[s.players[1].captain] = [];
    
    while(turnCount < s.players[0].stats.turns.length){
        for(let i = 0; i < s.players.length; i++){
            let p = s.players[i];
            let turn = p.stats.turns[turnCount];
            if(!turn.timeAtStartOfTurn) return false;
            lengths[p.captain].push(turn.timeAtStartOfTurn - lastTime);
            lastTime = turn.timeAtStartOfTurn;
        }
        turnCount++;
    }

    return lengths;
  
}

async function setupTurnStats(turn){
    for(let p of get(state).players){
        if(!p.stats) p.stats = {};
        if(!p.stats.turns) p.stats.turns = [false];
        p.stats.turns[turn] = {
            scoreAtStartOfTurn: await scorePlayer(p),
            timeAtStartOfTurn: Date.now(),
            deckSizeAtStartOfTurn: p.decks.hand.length + p.decks.discard.length + p.decks.deck.length,
            activations: 0
        }
        //if(!p.stats.activationsPerTurn[turn]) p.stats.activationsPerTurn[turn] = 0;
    }
}

// Re-enters the turn loop after a rollback has thrown 'cancelled'. Awaited, so the
// caller's await can't resolve while a game is still being played - but bounded,
// because if whatever threw is deterministic this recurses forever, and in BvB every
// await resolves as a microtask, so the browser never repaints: it reads as a hard
// freeze rather than a hang.
let respawnDepth = 0;
const MAX_RESPAWN = 20;

async function respawnTurns(){
    perfCount('turns.respawn');
    if(respawnDepth >= MAX_RESPAWN){
        throw new Error(`Turn loop respawned ${MAX_RESPAWN} times without finishing a turn: abandoning game`);
    }
    respawnDepth++;
    try{
        await takeTurns();
    }finally{
        respawnDepth--;
    }
}

export async function takeTurns(){
    if(isMe(get(state).currentPlayer) || isMe(get(state).activePlayer)){
        prepareContinuationReplay('takeTurns');
    }

    ui.update(ui => {
        ui.playing = true; // So you don't double-count
        return ui;
    })

    console.log('taking turns')
    if(isMe(get(state).players[0])) await setupTurnStats(0);

    if(isMe(get(state).players[0]) && !get(state).practice && !get(state).players.some(p => p.bot || p.started)){
        // Offer opportunity for network play
        let invite = await choose(`Game about to start: do you want to invite someone to play online?`, ['Yes: copy invite', 'No: play on one screen']);
        if(invite == 'Yes: copy invite'){
            await togglePublic();
            let onwards = false;
            let unsub = state.subscribe((s) => {
                if(s.players[1].username != get(prefs).username) advance('joined');
            })
            do{
                try{
                    await makeInvite(get(state).players[1].captain);
                    onwards = await choose(`Invite to play as ${get(state).players[1].captain} copied to clipboard: waiting for player to join...`, ['Copy again', 'Abandon and start game']);
                }catch(e){
                    await info('Unable to copy: click OK to try again');
                    onwards = 'Copy again';
                }
            }while(onwards == 'Copy again');
            unsub();
            if(onwards == 'joined') playSound('joined');
        }
    }

    await starterActions(); // Including drawing cards etc.
    let starterState = clone(get(state));
    res.update(r => {
        //r.initialState = initialState;
        r.savedState = starterState;
        r.prevStates = [starterState];
        return r;
    })
    await scoreGame(false); // 
    while(!get(state).ended && (get(state).gameEndTurn == -1 || get(state).turn <= get(state).gameEndTurn)){
        if(get(state).currentPlayer == 0) await setupTurnStats(get(state).turn);
        if(get(state).advertised){
            ui.update(ui => {
                ui.advertiseGame = false;
                return ui;
            })
        }
        if(get(res).BvB){
            // Only drives the progress readout; takeTurns scores properly once the
            // loop ends. Full scoring resolves every endgame effect on both players.
            // ponytail: every 5th turn, drop to end-of-game only if that's still slow
            if(get(state).turn % 5 == 0) await scoreGame();
            await tick();
        }
        let i = get(state).currentPlayer;
        //for(let i = get(state).currentPlayer; i < get(state).players.length; i++){
            /*if(i != 0 && get(state).practice){
                // Skip other players' turns
                state.update(s => {
                    s.phase = null;
                    s.step = null;
                    return s;
                })
                break; 
            }*/
            state.update(s => {
                s.currentPlayer = i;
                s.activePlayer = i;
                if(s.players[i].bot) s.botWorking = true;
                return s;
            });
            //updateState();
            //await changePlayer(i);
            await changePlayer(i);
            let p = get(state).players[get(state).currentPlayer];
            if(isMe(get(state).currentPlayer)){
                console.log('active player - taking turn', get(state));
                if(get(state).phase == null){
                    // Should I delete the below gettingToStep? When would it ever fire?
                    // Jan 2026: commented out so it always registers the step
                    // (on the basis that null phases only happen at handover)
                    /*if(!get(ui).gettingToStep)*/ log({event: 'playerTurn', turn: get(state).turn, player: get(state).currentPlayer});
                    state.update(s => {
                        s.phase = Object.keys(playerTurn)[0];
                        return s;
                    })
                    markUndoTurnStart();

                    // Jan 2026: risky adding of clear gettingToStep here
                    // on the basis that it should never be true
                    ui.update(ui => {
                        ui.gettingToStep = false;
                        return ui;
                    })
                }
                for(let phaseNum = 0; phaseNum < Object.keys(playerTurn).length; phaseNum++){
                    let phase = Object.keys(playerTurn)[phaseNum];
                    if(get(state).phase != phase) continue;
                    let stuffHappened = true;
                    if(!get(ui).gettingToStep) announce(`${upperInitial(phase)} phase`);
                    if(get(state).step == null){
                        if(!get(ui).gettingToStep) log({event: 'phase', phase}, false);
                        state.update(s => {
                            s.step = Object.keys(playerTurn[phase])[0];
                            return s;
                        })
                        stuffHappened = false; // Presume things happened before if not
                    }
                    for(let j = 0; j < Object.keys(playerTurn[phase]).length; j++){
                        let step = Object.keys(playerTurn[phase])[j];
                        if(get(state).step != step) continue;
                        if(!get(ui).gettingToStep){
                            closeWorkingLog();

                            res.update(r => {
                                if(!r.BvB){
                                    r.interimState = {
                                        state: clone(get(state)),
                                        undoLength: r.undos.length
                                    };
                                    r.savedState = clone(get(state));
                                    //r.prevStates = [...r.prevStates, clone(get(state))]; // necessary?
                                }
                                
                                if(typeof r.undoThreshold != 'number') r.undoThreshold = 0;
                                return r;
                            })
                            markContinuationCheckpoint(`${phase}:${step}`);

                            updateAllTransients(); // just to keep things nicely synced for e.g. wildcards
                            
                            sendUpdate(false, true); // This sends res too, but the specifics are filtered out in the code to keep it fairly wee

                            console.log('Next step of game: resetting cache', phase, step) // !!! TODO remove if this causes problems
                            state.update(s => {
                                s.cache = {
                                    allPrev: [],
                                };
                                delete s.payingCost;
                                //s.choices.thisTurn = [];
                                return s;
                            })
                        }
                        // Presume we are now where we want to be
                        ui.update(ui => {
                            ui.gettingToStep = false;
                            return ui;
                        })
                        let events = await playerTurn[phase][step](get(state).players[get(state).currentPlayer]);
                        // A manual action may have ended this step by skipping its prompt: let it
                        // resolve here, in this step, before moving on (see withTurnHeld)
                        await untilManualActionsDone();

                        if(endNow()) break;                          
                        if(events == 'spawned_new_turns'){
                            updateState();
                            return;
                        }
                        if(!isMe(get(state).currentPlayer)) continue; // In case you get bumped
                        /*if(events == 'restarting'){
                            return;
                        }*/
                        if(events != 'nothing_happened') stuffHappened = true;
                        if(j < Object.keys(playerTurn[phase]).length - 1){
                            state.update(s => {
                                s.step = Object.keys(playerTurn[phase])[j + 1];
                                return s;
                            });
                        }
                        
                    }
                    if(!isMe(get(state).currentPlayer)) continue; // In case you get bumped
                    if(endNow()) break;
                    if(!stuffHappened){
                        // Jan 2026: don't think the below gettingToStep should ever be possible either...?
                        /*if(!get(ui).gettingToStep)*/ logText(`---`, false, false);
                        //await info(`${phase[0].toUpperCase()}${phase.slice(1)} phase: nothing to do`);
                    }
                    if(phaseNum < Object.keys(playerTurn).length - 1){
                        state.update(s => {
                            s.phase = Object.keys(playerTurn)[phaseNum + 1];
                            s.step = null;
                            return s;
                        })
                    }
                }
                if(endNow()) break;
                if(isMe(get(state).currentPlayer)){ // Just to check it hasn't fallen through
                    //await sendQueue.onEmpty(); // So that I don't receive any of my own messages now
                    state.update(s => {
                        let nextI = i + 1;
                        if(nextI >= s.players.length || get(state).practice){
                            console.log('resetting next player', i)
                            nextI = 0;
                            s.turn++; // Added when the turnover was ditched below
                        }
                        s.activePlayer = nextI;
                        s.currentPlayer = nextI;
                        s.playerMessage = 'ready';
                        s.phase = null;
                        s.step = null;
                        return s;
                    })
                    await tick();
                    if(!isMe(get(state).currentPlayer)) await untilQueueEmpties(); // Jan 2026 added to ensure we are up to date first
                    updateState(true); // To pass over to next player
                    
                }

            }else{ // Cutting else to see if we can thus allow for fallthrough when a player takes over role
            // cutting i test below based on it maybe coming from PeerJS days?? Will reinstate otherwise
            //if(!isMe(get(state).currentPlayer) /*&& i < get(state).players.length - 1*/){
                
                //updateState(); // Because this comes at a point of handover?
                changeView(get(state).currentPlayer);
                console.log('not current player so handing over')
                ui.update(ui => {
                    ui.gettingToStep = false;
                    return ui;
                })
                await new Promise((resolve, reject) => {
                    let unsubscribe = state.subscribe(s => {
                        if(s.burn || s.domination || isMe(s.currentPlayer)){
                            unsubscribe();
                            resolve();
                        }
                    })
                })
                /*await until(async _ => { 
                    if(get(state).burn || get(state).domination) return true;
                    //await reactQueue.onEmpty(); // in case things change afterwards
                    return (isMe(get(state).currentPlayer)); // i.e. handle all reactions etc. separately //(isMe(state.activePlayer) && isMe(state.currentPlayer) && state.playerMessage == 'ready') 
                });
                state.update(s => {
                    s.playerMessage = '';
                    return s;
                })*/
                console.log('back to me!')
                playSound('handover');
                await untilQueueEmpties();
                //await reactQueue.onIdle(); // Get everything across as needed
                await tick(); // Make sure everything is in sync
                console.log(get(state));
                
                //}
            }
        //}
        // Below moved earlier to avoid double-db
        /*if(endNow()) break;
        state.update(s => {
            s.turn++;
            s.phase = null;
            s.step = null;
            s.currentPlayer = 0;
            s.activePlayer = 0;
            return s;
        });
        console.log(get(state));
        updateState(); // To sync end of turn. Why isn't it always working? Race condition?*/
    };
    if(!get(state).ended){ // No burn or reload
        disableSave();
        state.update(s => {
            s.startedGameEndActions = true;
            return s;
        })
        if(isMe(get(state).players[0])){
            await doGameEndActions();
            state.update(s => {
                if(!s.endNow) s.turn--; // Because otherwise it gets incremented wrongly 
                s.ended = true;
                return s;
            })
            updateState();
            enableSave(); // So that final state is saved
            updateState();  
        }else{
            await new Promise((resolve, reject) => {
                let unsubscribe = state.subscribe(s => {
                    if(s.gameEndActionsComplete){
                        unsubscribe();
                        resolve();
                    }
                })
            })
        }
        
    }
    disableSave();
    console.log(get(state));
    if(!get(res).BvB){
        if(get(state).gameShared && get(state).hostUsername == get(prefs).username) deleteItem('current_games', 'nanoid', get(state).gameID);
        await info(`Game ended (via ${get(state).gameEndTrigger})! Click to proceed to scoring.`, {finalMessage: true});
        if(get(state).restartTurns){
            // For if you force an undo
            state.update(s => {
                delete s.restartTurns;
                return s;
            })
            takeTurns();
            return;
        }
        if(get(state).campaign){
            await resolveCampaign();
        }
        get(ui).endGame();
    }else{
        await scoreGame();
    }
}

function me(){
    return get(state).players.find(p => !p.bot);
}

function bot(){
    return get(state).players.find(p => p.bot);
}

async function iWon(){
    await scoreGame();
    if(me().score > bot().score) return true;
    return false;
}

export async function resolveCampaign(){
    let won = await iWon();
    let addedCard = false;
    let possible = campaignRewards[get(state).players.find(p => p.bot).captain];
    if(!possible){
        alert('Opponent not yet in rewards system! Report as a bug');
        possible = [];
    }else{
        possible = possible[won ? 'win' : 'lose'];
    }
    let commonPoss = eligibleRewards(won);
    let extraChoices = [
        {
            name: 'Gain on track',
            value: 'gain_track'
        }
    ]
    /*if(!get(state).campaignInfo.games.some(g => g.reward.type == 'upgrade_reinforce')){
        extraChoices.push({
            name: 'Upgrade *Reinforce* (not implemented yet)',
            value: 'upgrade_reinforce'
        })
    }*/
    // !! TODO remove Starter from below if we reject this
    // !! TODO readd !c.suits.includes('location') if we want to disallow that
    let reservePoss = allCards().filter(c => c && c.deck == get(state).players[0].captain && (c.area == 'Reserve')).filter(c => !c.suits.includes('incident')).filter(c => !alreadyAddedCards().some(cd => cd.id == c.id));
    let starterPoss = allCards().filter(c => c && c.deck == get(state).players[0].captain && (c.area == 'Starter')).filter(c => !c.suits.includes('incident')).filter(c => !alreadyAddedCards().some(cd => cd.id == c.id));
    //reservePoss = reservePoss.sort((a,b) => a.area.localeCompare(b.area));
    let options = [...commonPoss]//, ...reservePoss];
    if(won){
        state.update(s => {
            s.campaignInfo.currentRank = nextRank(s.campaignInfo.currentRank);
            return s;
        });
    }
    let enemy = get(state).players.find(p => p.bot);
    let me = get(state).players.find(p => !p.bot);
    let game = {
        key: `${get(state).startDate} ${get(state).hostPlayer}`,
        enemy: enemy.captain,
        won,
        botLevel: enemy.botLevel,
        humanScore: me.score,
        botScore: enemy.score
    };
    if(won && get(state).campaignInfo.currentRank == 'Admiral'){
        await info('You completed the campaign successfully by being promoted to Admiral – well done!', {finalMessage: true})
        state.update(s => {
            s.campaignInfo.ended = true;
            s.campaignInfo.games = [...s.campaignInfo.games, game];
            return s;
        })
    }else if(get(state).campaignInfo.games.length == 9){
        await info(`You have played 10 games and completed the campaign, finishing at rank ${nextRank(get(state).campaignInfo.currentRank)}.`, {finalMessage: true});
        state.update(s => {
            s.campaignInfo.ended = true;
            s.campaignInfo.games = [...s.campaignInfo.games, game];
            return s;
        })
    }else{
        //let choice = 'Reserve'; 
        //let options = reservePoss;
        let chosenType = 'add_common';
        /*if(commonPoss.length > 0 && reservePoss.length > 0){
            choice = await choose(`Campaign reward: You can add a card to your Reinforcements deck before the next game. Choose where it should come from`, ['Reserve', 'Common cards'], {finalMessage: true});
        }else if(reservePoss.length > 0){
            await info(`Campaign reward: You did not pick up any eligible commons cards for this opponent, so your only option is to add a card from Reserve to the Reinforcements deck.`, {finalMessage: true})
        }else if(commonPoss.length > 0){
            await info(`Campaign reward: You do not have any remaining eligible Reserve cards, so your only option is to add a card from your purchased Common cards to the Reinforcements deck.`, {finalMessage: true})
            options = commonPoss;
            chosenType = 'add_common';
        }else{
            await info(`Campaign reward: You do not have any eligible Reserve or Commons cards, so you cannot any card to the Reinforcements deck this game.`, {finalMessage: true});
            chosenType = 'none';
        }*/
        if(get(state).campaignInfo && get(state).campaignInfo.liveLong){
            if(!won){
                await info(`Live Long and Try to Prosper: since you lost this game, your starting resources revert to their defaults.`, {finalMessage: true});
                state.update(s => {
                    delete s.campaignInfo.nextDilithium;
                    delete s.campaignInfo.nextLatinum;
                    return s;
                })
            }else{
                let possible = ['latinum', 'dilithium'].filter(r => !get(state).campaignInfo.hasOwnProperty('next'+r));
                if(possible.length == 0){
                    await info(`Live Long and Try to Prosper: since you won this game, both your starting resources remain depleted for the next game.`, {finalMessage: true});
                }else if(possible.length == 1){
                    await info(`Live Long and Try to Prosper: since you won this game, your starting [[${possible[0]}]] will be depleted for the next game.`, {finalMessage: true});
                    state.update(s => {
                        s.campaignInfo['next'+possible[0]] = 0;
                        return s;
                    })
                }else{
                    let chosen = await choose(`Live Long and Try to Prosper: since you won this game, you need to select a starting resource to deplete for the next game:`, possible.map(p => `[[${p}]]`), {finalMessage: true});
                    state.update(s => {
                        s.campaignInfo['next'+chosen] = 0;
                        return s;
                    })
                }
            }
        }
        if(get(state).campaignInfo && get(state).campaignInfo.twoWeeks){
            if(!won){
                await info(`Two Weeks to the Nearest Outpost: since you lost this game, *Reinforce* returns to your Starting deck.`, {finalMessage: true});
                state.update(s => {
                    delete s.campaignInfo.reinforceReserve;
                    return s;
                })
            }else{
                await info(`Two Weeks to the Nearest Outpost: since you won this game, *Reinforce* moves to your Reserve deck.`, {finalMessage: true});
                state.update(s => {
                    s.campaignInfo.reinforceReserve = true;
                    return s;
                })
            }
        }
        if(get(state).campaignInfo && get(state).campaignInfo.gazelle){
            if(won){
                let last = get(state).campaignInfo.games.length - 1;
                if(last > 0 && get(state).campaignInfo.games[last].won){
                    await info(`Running Like a Baby Gazelle: since you've won two games in a row, you'll start the next game with a random extra Incident in your deck.`, {finalMessage: true});
                    state.update(s => {
                        s.campaignInfo.startingIncident = true;
                        return s;
                    })
                }else{
                    await info(`Running Like a Baby Gazelle: you've not won two games in a row yet, so no extra starting Incident next game.`, {finalMessage: true});
                }
            }else{
                await info(`Running Like a Baby Gazelle: since you lost this game, you won't start the next with a random extra Incident.`, {finalMessage: true});
                state.update(s => {
                    delete s.campaignInfo.startingIncident;
                    return s;
                })
            }
        }

        if(get(state).campaignInfo && get(state).campaignInfo.acquire){
            if(won){
                await info(`Rules of Acquisition: since you won this game, you can only choose to add a card, not an alternative bonus`, {finalMessage: true});
                //options = commonPoss;
            }else{
                await info(`Rules of Acquisition: since you lost this game, you can choose any bonus (adding a card or alternatives)`, {finalMessage: true});
            }
        }
        
        let alternative = campaignRewards[get(state).players.find(p => p.bot).captain][(won ? 'win' : 'lose')+'_alt'];
        let route = {
            text: `Add a card from Commons to Reinforcements deck (${options.length} available)`,
            type: 'add'
        };
        if(alternative && !get(state).campaignInfo.acquire){
            if(!Array.isArray(alternative)) alternative = [alternative];
            alternative = [
                route,
                ...alternative
            ];
            if(alternative.some(a => a.type == 'add_reserve')){
                let res = alternative.find(a => a.type == 'add_reserve');
                res.options = [];
                if(!res.area){
                    res.area = ['Reserve', 'Starter']; // !!! whilst I wait for clarification from Dávid
                }else{
                    if(res.area.includes('Reserve')){
                        if(res.cardType) reservePoss = reservePoss.filter(c => matches(c, res.cardType))
                        options = [...options, ...reservePoss];
                        res.options = [...res.options, ...reservePoss];
                    }
                    if(res.area.includes('Starter')){
                        if(res.cardType) starterPoss = starterPoss.filter(c => matches(c, res.cardType))
                        options = [...options, ...starterPoss];
                        res.options = [...res.options, ...starterPoss];
                    }
                }
                if(res.num && res.num != 1){
                    // Could pick multiple, so need to mark as separate option
                }else{
                    route.text = `Add card(s) to Reinforcements deck (${options.length} available)`;
                    alternative = alternative.filter(a => a.type != 'add_reserve');
                }
                
            }
            let choice = alternative[0].text;
            if(alternative.length > 1){
                choice = await choose(`Choose a campaign reward`, alternative.map(a => a.text), {card: options, finalMessage: true});
            }else{
                await info(`Campaign reward: your only possible choice is to ${choice}`, {finalMessage: true})
            }
            if(choice){
                route = alternative.find(a => a.text == choice);
            }
        }
        if(route.type == 'add'){
            if(options.length > 0){
                // How to handle Georgiou??
                addedCard = await chooseCards(`Campaign reward: You can add a card to your Reinforcements deck before the next game. Choose:`, options, {finalMessage: true, lookSpecific: true});
                if(typeof addedCard == 'string'){
                    chosenType = addedCard;
                }else if(addedCard.deck == 'Common'){
                    chosenType = 'add_common';
                }else{
                    chosenType = 'add_reserve';
                }
            }else{
                await info(`Campaign reward: You do not have any eligible cards to add to the Reinforcements deck this game.`, {finalMessage: true});
                chosenType = 'none'; 
            }
        }else if(route.type == 'add_reserve'){
            // Possibly more than one
            let show = await resolveNum(route.num);
            addedCard = await chooseCards(`Campaign reward: You can add ${typeof show === 'object' ? show.text : show} card(s) to your Reinforcements deck before the next game. Choose:`, route.options, {finalMessage: true, lookSpecific: true, number: route.num});

        }else{
            chosenType = route.type;
        }
        
        /*if(choice == 'Common cards'){
            chosenType = 'add_common';
            options = commonPoss;
        }
        if(chosenType != 'none'){
            addedCard = await chooseCards(`Select a card to add to Reinforcements deck`, options, {finalMessage: true, lookSpecific: true});
        }*/
        game.reward = {
            type: chosenType
        };
        if(chosenType == 'add_reserve' || chosenType == 'add_common'){
            game.reward.card = possArray(addedCard).map(c => { return {name: c.name, id: c.id} });
            if(game.reward.card.length == 1) game.reward.card = game.reward.card[0];
        }else if(chosenType == 'gain_track'){
            let amount = won ? 1 : 3;
            game.reward.amount = amount;
            if(!won){
                if(get(state).campaignInfo.captain == 'Borg Queen'){
                    game.reward.track = 'collective';         
                }else{
                    let tracks = ['research', 'influence', 'military', 'collective'];
                    let track = await choose(`Select which track to gain ${amount} on at the start of the next game`, tracks.map(t => `[[${t}]]`), {finalMessage: true});
                    let t = tracks.find(t => track == `[[${t}]]`);
                    game.reward.track = t;
                }
            }
        }else{
            game.reward = {...route};
        }
        state.update(s => {
            s.campaignInfo.nextBotLevel = getCampaignBotLevel(s.campaignInfo.currentRank, s.campaignInfo.level);//nextBotLevel(get(state).players[1].botLevel);
            s.campaignInfo.games = [...s.campaignInfo.games, game];
            return s;
        })
    }
}

async function doGameEndActions(){
   // if(get(state).gameEndTrigger != 'Burn'){
        // before scoring actions only happen if no Burn?
  //  }
  
  for(let i = 0; i < get(state).players.length; i++){
    let p = get(state).players[i];
    let toReturn = 0;
    openWorkingLog();
    logText(`Doing game-end actions for ${p.captain}.`)
    //p.locationScoring = 0;
    /*for(let c of getNeutralZone()){

            let tokens = countPlayerTokens(c, p);
            if(!tokens) continue;
            if(!ongoingPlayer(p).includes('no_nz_scoring')){
                logText(`Gaining ${tokens} [[glory]] for tokens on ${cc(c.uniqueID)}`);
                //await doAction(p, 'gain_tokens', {token: 'glory', amount: tokens })
                p.locationScoring++;
            }
            
            
    }*/
    for(let c of [...allScoreableCards(p), ...(p.dashboard.passives || [])].filter(c => ongoingEffects(c).includes('bot_destroy'))){
        let effect = ongoingEffects(c, 'bot_destroy')[0];
        if(p.bot){
            logText(`Destroying card ${cc(c.uniqueID)} before scoring because the bot has it`);
            await doAction(p, 'destroy', {obj: c.uniqueID});
        }else if(effect.log && get(state).cardLog[c.uniqueID].deck == 'log'){
            logText(`Destroying card ${cc(c.uniqueID)} before scoring because it is logged`);
            await doAction(p, 'destroy', {obj: c.uniqueID});
        }    
    }
    closeWorkingLog();
    changePlayer(p);
    for(let c of [...allScoreableCards(p), ...(p.dashboard.passives || [])]){
        for(let e of ongoingEffects(c, 'endgame_incident_return')){
            if(typeof e == 'number'){
                toReturn += e;
            }else{
                console.log('Help! not a number for incident return')
            }
        }
        
    }
    
    openWorkingLog();
    logText(`Doing game-end actions for ${p.captain}.`)
    if(toReturn){
        let incidents = allScoreableCards(p).filter(c => c.suits.includes('incident'));
        if(!p.bot){
            if(isMe(p)){
                await doAction(p, 'endgame_return', {toReturn});
            }else{
                await offerEffect(p, otherPlayer(p), {uniqueID: -1}, {type: 'action', verb: 'endgame_return', toReturn}, false)
            }
            //incidents = await chooseCards(`Select up to ${toReturn} [[incident]] to return`, incidents, {lookSpecific: true, number: {min: 0, max: toReturn}});
            //logText(`Returning ${incidents.length} [[incidents]] thanks to card special effects`)
        }else{
            incidents = incidents.slice(0, toReturn);
        }
    }
    
    changePlayer(p); // So this player is the active player for the below
    for(let full of ongoingPlayerWithCards(p, 'before_scoring')){
        await resolveEffects(full.card, full.effects, full.effects.attack);
    }
    
    /*for(let i of incidents){
        await doAction(p, 'return', {obj: i.uniqueID});
    }*/
    closeWorkingLog();
  }
  state.update(s => {
    s.gameEndActionsComplete = true;
    return s;
  })
}

let playerTurn = {
    resupply: {
        turn_beginning: async (p) => {
            openWorkingLog();
            try{
                let effects = () => {
                    let toCheck = cardsInPlay(p, false);
                    if(badlands()) toCheck = [...toCheck, badlands()];
                    let reactions = [];
                    for(let c of toCheck){
                        if(ongoingEffects(c).includes('turn_condition')){
                            let effects = ongoingEffects(c, 'turn_condition');
                            for(let e of effects){
                                reactions.push({
                                    card: c.name,
                                    text: e.text,
                                    p: getPlayerIndex(p),
                                    condition: () => isEligibleForReaction(c, e.type),
                                    effect: async () => {
                                        resetCache();
                                        await resolveEffects(c, e, e.attack); // right?
                                    }
                                })
                            }
                        }
                    }
                    return reactions;
                }

                let turnEffects = effects();
                
                await freeOrderActions(turnEffects, 'Turn beginning', false, effects);
            }catch(e){
                if(e == 'cancelled'){
                    closeWorkingLog();
                    state.update(s => {
                        return cleanupBackState();
                    })
                    await respawnTurns();
                    return 'spawned_new_turns';
                }else{
                    throw e;
                }
                
            }
            
            closeWorkingLog();
        },
        resupply: async (p) => {
            if(p.bot){
                disableSave();
                if(p.captain == 'Boimler'){
                    openWorkingLog();
                    logText('Boimler special rule: discarding top of deck to gain card');
                    if(p.decks.deck.length == 0){
                        logText(`Deck empty, so reshuffling`);
                        await reshuffleDeck(p, {});
                    }
                    if(p.decks.deck.length > 0){
                        let c = p.decks.deck[0];
                        await doAction(p, 'discard', {obj: c.uniqueID});
                        if(matches(c, ['ship', 'ally'])){
                            logText(`Matches [[ship]]/[[ally]], so gaining card to Supplement deck`);
                            let toTake = getMarketCards().find(card => card.suits[0] == c.suits[0]);
                            if(toTake){
                                await doAction(p, 'gain', {obj: toTake.uniqueID});
                                await moveTo(toTake, `players.reserve.${getPlayerIndex(p)}`, 0);
                            }
                        }else if(matches(c, ['person', 'cargo'])){
                            logText(`Matches [[person]]/[[cargo]], so taking corresponding Market card`);
                            let toTake = getMarketCards().find(card => card.suits[0] == c.suits[0]);
                            if(toTake){
                                await doAction(p, 'take', {obj: toTake.uniqueID});
                            }
                        }else if(matches(c, 'incident')){
                            logText(`[[incident]], so returning it, gaining 1 [[glory]] and logging top of deck`);
                            await doAction(p, 'return', {obj: c.uniqueID});
                            await doAction(p, 'gain_tokens', {token: 'glory', amount: 1});
                            try{
                                if(p.decks.deck.length == 0){
                                    logText(`Deck empty, so reshuffling`);
                                    await reshuffleDeck(p, {});
                                }
                                if(p.decks.deck.length > 0){
                                    let c = p.decks.deck[0];
                                    await doAction(p, 'log', {obj: c.uniqueID});
                                } 
                            }catch(e){
                                if(e != 'uncompleted') throw e;
                            }
                        }else{
                            logText(`Doesn't match relevant suit, so doing nothing`)                      
                            /*try{
                                if(p.decks.deck.length == 0){
                                    logText(`Deck empty, so reshuffling`);
                                    await reshuffleDeck(p, {});
                                }
                                if(p.decks.deck.length > 0){
                                    let c = p.decks.deck[0];
                                    await doAction(p, 'log', {obj: c.uniqueID});
                                } 
                            }catch(e){
                                if(e != 'uncompleted') throw e;
                            }
                            await doAction(p, 'gain_tokens', {token: 'glory', amount: 1})
                            */
                        }
                    }
                    await doActionPause();
                    closeWorkingLog();
                }
                if(p.captain == 'Eddington'){
                    openWorkingLog();
                    try{
                        logText(`Eddington special rule: discarding top card of deck`);
                        
                        await resolveAction(p, p.decks.captain[0], {
                            type: 'action',
                            obj: {
                                defaultNum: true,
                                num: 1,
                                card: {
                                type: null,
                                placement: 'top',
                                num: null,
                                place: 'deck',
                                owner: 'bot'
                                }
                            },
                            verb: 'discard',
                            actor: null,
                            text: 'discard the top card of the bot deck'
                        }, false);
                        let bl = badlands();
                        if(bl && canControlLocation(p, bl) && p.decks.reserve.length > 0){
                            logText(`Bot has secured *The Badlands*, so logging top card of Supplement deck`);
                            await doAction(p, 'log', {obj: p.decks.reserve[0].uniqueID});
                        }
                    }catch(e){
                        if(!['uncompleted','completed'].includes(e)) throw e;
                    }
                    closeWorkingLog();
                }
                if(p.captain == 'Dukat'){
                    openWorkingLog();
                    logText('Dukat special rule: discarding top 3 cards of deck');
                    for(let i = 0; i < 3; i++){
                        await resolveAction(p, p.decks.captain[0], {
                            type: 'action',
                            obj: {
                                defaultNum: true,
                                num: 1,
                                card: {
                                type: null,
                                placement: 'top',
                                num: null,
                                place: 'deck',
                                owner: 'bot'
                                }
                            },
                            verb: 'discard',
                            actor: null,
                            text: 'discard the top card of the bot deck'
                        }, false);
                    }
                    closeWorkingLog();
                }
                if(p.captain == 'Shaw' && p.currentChart == 'seven'){
                    openWorkingLog();
                    await doAction(p, 'gain_lowest', 
                        {
                            obj: {
                                defaultNum: true,
                                num: 1,
                                types: [
                                    'research',
                                    'influence',
                                    'military'
                                ]
                            },
                        }
                    )
                    await doActionPause();
                    closeWorkingLog();
                }
                return;
            }
            if(get(prefs).turnSaves) disableSave();
            p.firstAction = true; // Specifically for 'as your first action of turn' effects
            if(get(state).turn != 1 && ongoingPlayer(p).includes('acquisition_rules')){
                let rules = p.decks.fleet.filter(c => c.traits.includes('rule of acquisition'));
                let activeIndex = rules.findIndex(c => c.active);
                activeIndex++;
                if(activeIndex >= rules.length) activeIndex = 0;
                let active = rules[activeIndex];
                if(ongoingPlayer(p).includes('select_acquisition')){
                    active = await chooseCards(`Select Rule of Acquisition to follow this turn`, rules);
                }
                for(let r of rules){
                    r.inactive = true;
                }
                delete active.inactive;
                active.active = true;
            }
            let effects = () => {
                let eligible = [...cardsInPlay(p, false), ...getNeutralZone().filter(c => !canGainControl(c))].filter(card => getCardEffect(card, 'resupply').filter(r => validEffect(card, r, p) && meetsThreshold(p, r)).length > 0);
                let res = [];
                for(let c of eligible){
                    /*// Rewrite here with freeOrderActions
                    if(c.suits.includes('captain') && ongoingPlayer(p).includes('ignore_captain_resupply')){
                        let choice = await choose(`You may ignore your [[captain]]'s resupply operation. What do you want to do?`, ['Proceed with Resupply', 'Ignore Resupply']);
                        if(choice != 'Proceed with Resupply') continue;
                    }
                    await doAction(p, 'resupply', {card: c.uniqueID, obj: c.uniqueID, auto: true});
                    await doActionPause();
                    //await autoCardActions(c, getHints(c, 'resupply'));*/
                    // One concern: why was I using doAction before?
                    let hints = getHints(c, 'resupply');
                    if(!hints) continue;
                    for(let h of hints){
                        let check = false;
                        if(c.suits.includes('captain') && ongoingPlayer(p).includes('ignore_captain_resupply')){
                            check = `Your [[captain]]'s resupply operation is optional: proceed with it?`;
                        }
                        res.push({
                            card: c.name,
                            text: h.text,
                            p: getPlayerIndex(p),
                            check,
                            condition: () => isEligibleForReaction(c, 'resupply'),
                            effect: async () => {
                                logText(`Resupply effect for ${c.name}: `)
                                await autoCardActions(c, [h]); //
                                //await resolveEffects(c, e, e.attack); // right?
                            }
                        })
                    }
                }
                return res;
            }

            let resupply = effects();

            
            if(resupply.length > 0){
                openWorkingLog();
                await freeOrderActions(resupply, 'Resupply', true, effects);
                closeWorkingLog();
            }else{
                return 'nothing_happened';
            }
            
        },
    },
    control: {
        locations: async (p) => {
            if(get(state).skipNextControl){
                logText('Skipping Location control step due to previous card effect');
                state.update(s => {
                    delete s.skipNextControl;
                    return s;
                })
                return;
            }
            
            let eligible = getNeutralZone().filter(c => canGainControl(c) && canControlLocation(p, c));
            let ineligible = getNeutralZone().filter(c => canGainControl(c) && !eligible.includes(c));
           /* if(eligible.length == 1){
                let c = eligible[0];
                await claimLocation(p, c);
            }else*/ 
            if(ongoingPlayer(p).includes('secure_all_cubes')){
                // During your Control Step, any neutral [[location]] with a [[cube]] and ^not^ secured by your opponent is secured by you.
                let extras = ineligible.filter(c => !canControlLocation(otherPlayer(p), c) && shipsAtLocation(c, p).some(ship => matches(ship, 'cube')));
                eligible = [...eligible, ...extras];
            }
            let didSomething = false;
            if(eligible.length > 0){
                didSomething = true;
                if(eligible.length == 1){
                    ui.update(ui => {
                        ui.selection = eligible[0];
                        return ui;
                    })
                }
                let l = botFavouriteCards(p, eligible)[0];//eligible.sort((a,b) => botValue(b, p) - botValue(a, p))[0];
                let num = 1;
                if(ongoingPlayer(p).includes('extra_control')){
                    let t = ongoingPlayer(p, 'extra_control');
                    if(typeof t.num == 'number'){
                        num = {max: t.num};
                    }else{
                        num = t.num;
                    }
                }
                if(!p.bot){
                    let numText = num;
                    if(typeof num == 'object') numText = await resolveNum(num).text;
                    l = await chooseCards(`You may select ${numText} secured neutral [[location]] to take control of`, eligible, {optional: true, number: num}); 
                }
                let count = 0;
                if(l && l != 'cancelled'){
                    count = l.length;
                    for(let c of possArray(l)){
                        try{
                             await doAction(p, 'gain_control', {obj: c.uniqueID});
                        }catch(e){
                            if(e == 'cancelled'){
                                count--;
                            }else if(e != 'uncompleted' && e != 'completed') throw e; // Move past others
                        }
                       
                    }
                    //await claimLocation(p, l);
                    for(let c of cardsInPlay(p, false)){
                         let t = ongoingEffects(c, 'extra_control');
                         for(let trig of t){
                            if(testNum(trig.num, count)){
                                await resolveEffects(c, trig.action, trig.action.attack)
                            }
                        }
                    }                           
                }
                if(endNow()) return;
            }

            for(let c of cardsInPlay(p, false)){
                if(ongoingEffects(c).includes('timing_trigger')){
                    // TODO Make this reorderable
                    let tt = ongoingEffects(c, 'timing_trigger')[0];
                    if(tt.step == 'control'){
                        await resolveEffects(c, tt.action, tt.action.attack) // false to action always?
                    }
                }
            }

            if(!didSomething) return 'nothing_happened';
        }
    },
    action: {
        playing: async (p) => {
            
            if(!p.bot){
                if(!get(prefs).turnSaves) enableSave();
                if(!p.dummy){
                    res.update(r => {
                        r.undoThreshold = r.prevStates.length; //= r.undos.length;
                        return r;
                    })
                    await info('Action phase: press OK to end your turn', {skippable: false, turnMarker: true});
                }else{
                    for(let c of cardsInPlay(p, false)){
                        p = getP(p); // Just to keep up to date...
                        if(canActivate(c, p) && !c.activated){
                            try{
                                await autoCardActions(c, getHints(c, 'activate'));
                            }catch(e){
                                if(e != 'cancelled') throw e;
                            }
                            
                        }
                        closeAllLooks(false);
                    }
                    while(p.decks.hand.length > 0 && p.decks.hand.some(c => canPlay(p, c))){
                        let poss = p.decks.hand.filter(c => canPlay(p, c));
                        let i = Math.floor(prng() * poss.length);
                        try{
                            await autoCardActions(poss[i], getHints(poss[i], 'play'));
                        }catch(e){
                            if(e != 'cancelled') throw e;
                        }
                        closeAllLooks(false);
                        
                    }
                }
                    /*if(get(ui).restartTurns){
                    ui.update(ui => {
                        ui.restartTurns = false;
                        return ui;
                    })
                    return 'restarting';
                }*/
                closeWorkingLog();
            }else{
                if(get(state).ended) return; // To stop additional extra things accidentally at end
                //logText('Drawing cards for actions');
                for(let i = 0; i < howManyBotActions(p, get(state)); i++){
                    await doAction(p, 'draw', {obj: {num: 1}, intoPlay: true, toEnd: true});
                    //await drawCard(p, 1, {intoPlay: true, toEnd: true});
                    if(endNow()){
                        // Only spot where botWorking is still set from the turn start: leave it
                        // on and advance() refuses the final click through to scoring
                        state.update(s => {
                            s.botWorking = false;
                            return s;
                        })
                        return;
                    }
                }
                for(let c of [...p.decks.play]){
                    // Play the card(s) drawn. A card can be forced out of play by an earlier
                    // resolution (e.g. Bassen Rift making the Bot discard), so skip it if so
                    if(!getP(p).decks.play.some(pc => pc.uniqueID == c.uniqueID)){
                        logText(`${cc(c.uniqueID)} is no longer in play, so not resolving it`);
                        continue;
                    }
                    await resolveBotCard(p, c);
                    if(endNow()) return; // A burn ends the game where it happens, as it does for the human
                }
                // Now we do the Q discard reaction thing too
                if(get(state).players.some(p => !p.bot && p.captain == 'Q')){
                    await checkForReactions(p, 'opp_cleanup_interrupt', {}, 'before');
                    // The above gives the opponent a chance to teleport a Q to a card before it's discarded
                    if(endNow()) return;
                }
                let toDiscard = [];
                let qvisiting = [];
                p = getP(p); // Just to keep up to date...
                for(let c of p.decks.play){
                    if(c.visiting && c.visiting.length > 0){
                        qvisiting = [...qvisiting, c.uniqueID];
                        continue;
                    }
                    toDiscard = [...toDiscard, c];
                }
                if(qvisiting.length > 0){
                    logText(`${qvisiting.map(c => `${cc(c)}`)} were not dismissed because they have [[qsuit]] visiting`);
                }
                for(let c of toDiscard){
                //while(p.decks.play.length > 0){
                    await moveTo(c, `players.discard.${getPlayerIndex(p)}`, 0);
                    console.log(p.decks.play.map(c => c.name).join(','));
                }
                await doActionPause();
                if(p.captain == 'Khan' && p.currentChart == 'exile'){
                    await doAction(p, 'gain_tokens', { token: 'dilithium', amount: 1});
                    await doActionPause();
                    if(p.dilithium >= 5){
                        logText('Khan has reached 5+ [[dilithium]]: spending them all to gain [[encounter]] and leave exile');
                        await doAction(p, 'spend', {obj: {num: 'all', type: 'tokens', specific: 'dilithium'}});
                        await doActionPause();
                        if(get(state).commons.encounters.length > 0){
                            await doAction(p, 'gain_card', {obj: get(state).commons.encounters[0].uniqueID});
                        }else{
                            logText('Unable to gain top [[encounter]] card: deck empty')
                        }
                        // Swap to other card
                        p.currentChart = 'regular';
                    }
                    await doActionPause();
                }
                if(p.captain == 'Borg Queen'){
                    if(p.decks.captain[0].drones[p.captain] >= 8){
                        logText(`Borg Queen has 8+ [[drone]] on Captain card, so taking [[incident]]`)
                        await resolveAction(p, p.decks.captain[0], {
                            type: 'action',
                            obj: {
                                num: 1,
                                types: 'incident'
                            },
                            verb: 'take',
                            actor: null,
                            text: 'take an [[incident]]'
                        })
                    }
                }
                /*if(badlands()){
                    // Check Badlands effect (hard-coded)
                    let opp = get(state).players.find(pl => pl.captain != p.captain);
                    if(!canControlLocation(opp, badlands())){
                        logText(`Human player does not control *Badlands*, so Bot gains a [[glory]].`)
                        await doAction(p, 'gain_tokens', {token: 'glory', amount: 1, fromSupply: true})
                    }
                }*/
            }
            
        }
    },
    cleanup: {
        cleanup_start: async (p) => {
            // Only feeds undo/save, neither of which BvB has.
            if(!get(res).BvB) ui.update(ui => {
                ui.savedState = clone(get(state));
                return ui;
            });
            disableSave();
            
        },
        turn_end: async (p) => {
            if(p.bot) return;
            await checkTurnEndEffects(p);
        },
        cleanup_effects: async (p) => {   
            if(p.bot){
                if(badlands()){
                    openWorkingLog();
                    let hints = getHints(badlands(), 'cleanup');
                    if(!hints) return;
                    for(let h of hints){
                        await autoCardActions(badlands(), [h]);
                    }
                    closeWorkingLog();
                }
                return;
            }
            openWorkingLog();
            try{
                let effects = () => {
                    let toCheck = cardsInPlay(p, false);
                    if(badlands()) toCheck = [...toCheck, badlands()];
                    let reactions = [];
                    for(let c of toCheck){
                        if(ongoingEffects(c).includes('cleanup_condition')){
                            let effects = ongoingEffects(c, 'cleanup_condition');
                            for(let e of effects){
                                reactions.push({
                                    card: c.name,
                                    text: e.text,
                                    p: getPlayerIndex(p),
                                    condition: () => isEligibleForReaction(c, 'cleanup'),
                                    effect: async () => {
                                        resetCache();
                                        await resolveEffects(c, e, e.attack); // right?
                                    }
                                })
                            }
                        }
                        let hints = getHints(c, 'cleanup');
                        if(!hints) continue;
                        //if(ongoingEffects(c).includes('late_cleanup')) continue;
                        if(hints.length > 0){ // For all other cards
                            for(let h of getHints(c, 'cleanup')){
                                if(!h.text) continue; // Anything that doesn't have text isn't relevant here
                                // e.g. Astrometrics Lab. if this is a problem, can redo later
                                reactions.push({
                                    card: c.name,
                                    text: h.text,
                                    p: getPlayerIndex(p),
                                    condition: () => isEligibleForReaction(c, 'cleanup'),
                                    effect: async () => {
                                        logText(`Cleanup effect for ${c.name}: `);
                                        await autoCardActions(c, [h]);
                                    }
                                })
                            }
                            
                        }// 
                    }
                    return reactions;
                }

                let cleanupEffects = effects();
                
                await freeOrderActions(cleanupEffects, 'Cleanup', false, effects);
            }catch(e){
                if(e == 'cancelled'){
                    closeWorkingLog();
                    state.update(s => {
                        return cleanupBackState();
                    })
                    await respawnTurns();
                    return 'spawned_new_turns';
                }else{
                    throw e;
                }
                
            }
            
            closeWorkingLog();
        },
        stardate: async (p) => {
            if(p.bot){
                state.update(s => {
                    s.botWorking = true;
                    return s;
                })
            }
            await resolveStardateCards();
            if(get(state).practice){
                let card = await chooseCards(`Choose a card in the market to wipe`, getMarketCards(),  {cancellable: true, cancel: 'Go back'});
                if(card == 'cancelled'){
                    state.update(s => {
                        return cleanupBackState();
                    })
                    await respawnTurns();
                    return 'spawned_new_turns';
                }
                if(card){
                    await doAction(p, 'junk', {card: card.uniqueID});
                }
            }
            if(endNow()) return;
        },
        glory: async (p) => {
            if(!p.bot){
                let token = playerResourceToken(p);
                let card = await chooseCards(`Choose a card in the market to place ${token.amount} [[${token.token}]] token on`, getMarketCards(), {cancellable: true, cancel: 'Go back'});
                if(card == 'cancelled'){
                    state.update(s => {
                        return cleanupBackState();
                    })
                    await respawnTurns();
                    return 'spawned_new_turns';
                }
                if(token.token != 'glory'){
                    // Take a glory from the supply anyway
                    await takeFromGloryPile(1);
                }
                if(card){
                    await doAction(p, 'token_on_card', {card: card.uniqueID, ...token, from: 'supply'});
                }
                // Filter out prevStates
                res.update(r => {
                    r.prevStates = r.prevStates.slice(-1); // To reduce size
                    return r;
                })
            }else{
                // Glory
                let o = get(state).players.find(o => o.captain != p.captain); // Only for 2-player, but that's ok
                let card = botLeastValuableCard(o, getMarketCards()); // Least valuable for human; ties broken person > cargo > ship > ally
                let token = playerResourceToken(p);
                if(token.token != 'glory'){
                    // Take a glory from the supply anyway
                    await takeFromGloryPile(1);
                }
                console.log(p.decks.play.map(c => c.name).join(','));
                if(card){
                    openWorkingLog();
                    await doAction(p, 'token_on_card', {card: card.uniqueID, ...token, from: 'supply'});
                    await doActionPause();//await waitMillis(300);
                    state.update(s => {
                        s.botWorking = false;
                        return s;
                    })
                    if(!get(res).BvB) await info(`Click to advance to the next player's turn`, {skippable: false});
                    closeWorkingLog();
                }
            }
            
        },
        discard: async (p) => {
            if(p.bot) return;
            pauseUpdates();
            let discarded = [];
            let qvisiting = [];
            
            let judgment = ongoingPlayerWithCards(p).filter(o => o.effects.includes('judgment_step'))
            if(judgment.length > 0 && p.judgment_effects?.effects){
                // Judgment step replaces Discard: the late_cleanup passive above already
                // handles the play area (Staging Area) and hand, so skip the standard dismissal,
                // hand-discard prompt
                await resolveEffects(judgment[0].card, p.judgment_effects.effects[0].effect, false);
                unPauseUpdates();
                return;
            }
            
            if(p.captain != 'Q' && get(state).players.some(pl => pl.captain == 'Q')){
                await checkForReactions(p, 'opp_cleanup_interrupt', {}, 'before');
                // The above gives the opponent a chance to teleport a Q to a card before it's discarded
            }
            
            for(let c of p.decks.play){
                if(c.visiting && c.visiting.length > 0){
                    qvisiting = [...qvisiting, c.uniqueID];
                    continue;
                }
                discarded = [...discarded, c.uniqueID];
                await moveTo(c, 'players.discard.'+getPlayerIndex(p));
                await tick();
                await updateAllTypeGains();
                updateAllTransients();
                await doActionPause();//new Promise(r => setTimeout(r, 300));
            }
            if(discarded.length > 0){
                logText(`Dismissed ${discarded.length} cards from play: ${discarded.map(c => `${cc(c)}`)}`);
            }
            if(qvisiting.length > 0){
                logText(`${qvisiting.map(c => `${cc(c)}`)} were not dismissed because they have [[qsuit]] visiting`);
            }
            if(p.decks.hand.length > 0){
                let toDiscard = [];
                if(p.dummy){
                    toDiscard = p.decks.hand;
                }else{
                    toDiscard = await chooseCards('You may discard any number of cards from your hand', p.decks.hand.filter(c => !ongoingEffects(c).includes('cannot_discard')), {number: 'any', extraChoices: [{name: 'Discard all', value: p.decks.hand.filter(c => !ongoingEffects(c).includes('cannot_discard')), condition: (ui, state) => {return true}}]});
                }
                if(toDiscard && toDiscard != 'cancelled'){
                    if(toDiscard.length > 0){
                        logText(`Discarded ${toDiscard.length} cards from hand: ${toDiscard.map(c => cc(c.uniqueID))}`);
                        for(let c of toDiscard){
                            await moveTo(c, 'players.discard.'+getPlayerIndex(p));
                            await tick();
                            await updateAllTypeGains();
                            updateAllTransients();
                            await doActionPause();//new Promise(r => setTimeout(r, 300));
                        }
                    }
                }
            }
            unPauseUpdates();
        },
        draw: async (p) => {
            if(p.bot){
                enableSave();
                return;
            }
            /*if(ongoingPlayer(p).includes('judgment_step')){
                // Judgment step replaces Discard & Draw: Q keeps its hand, no draw-to-hand-size.
                enableSave();
                return;
            }*/
            // New version that respects hand size with taking incidents
            //let hs = await handSize(p);
            pauseUpdates();
            if(faceupDraw(p) || true){
                // Special situation of face-up draw
                await drawToHandSize(p);
                
            }else{
                while(await handSize(p) > p.decks.hand.length){
                    let drawn = await drawCard(p, 1, {});
                    if(!drawn || drawn.length == 0) break;
                    //await doAction(p, 'draw', params);
                    //if(!params.drawn || params.drawn.length == 0) break;
                }
            }
            
            // !! TODO handle Riker etc.
            
            /*let num = await handSize(p) - p.decks.hand.length;
            if(num > 0){
                let cardsDrawn = await doAction(p, 'draw', {obj: {num}});
            }*/
            if(p.tempHandSizeIncrease) delete p.tempHandSizeIncrease; // Only lasts until drawing finished
            //await info(`Click to advance to the next player's turn`, {skippable: false});
            //logText(`Drawn ${cardsDrawn.length} cards: ${cardsDrawn.map(c => `$|${c.uniqueID}|$`)}`);
            enableSave();
            unPauseUpdates();
        },
        refresh: async (p) => {
            if(p.bot){
                /*for(let d of p.decks.duty){
                    // In case of Shaw action
                    if(d.activated) await doAction(p, 'refresh', {obj: d.uniqueID});//await deactivateCard(d);
                }*/
                return;
            }
            pauseUpdates();
            for(let c of cardsInPlay(p, false)){
                //delete c.activated;
                if(c.activated){
                    try{
                        await doAction(p, 'refresh', {obj: c.uniqueID}); //deactivateCard(c);
                        if(ongoingEffects(c).includes('cleanup_refresh_flip')){
                            // Flip card
                            unPauseUpdates();
                            await doAction(p, 'flip', {obj: c.uniqueID});
                        }
                    }catch(e){
                        if(e != 'uncompleted') throw e;
                    }
                }
            }
            //p.actions = 3;
            if(!ongoingPlayer(p).includes('q_action')){
                p.actions = getActionsPerTurn(p);
                if(get(state).shorterTurns) p.actions = 2;
            }
            
            p.activations = get(prefs).numActivations;
            unPauseUpdates();
        }
    }
}

async function checkTurnEndEffects(p){
    for(let c of cardsInPlay(p, false)){
        let effects = ongoingEffects(c, 'turn_end');
        //console.log(c, effects);
        for(let e of effects){
            if(e == 'attack_log'){
                // Check whether to log or not
                console.log(`Checking whether to log ${c.name} ${p.captain}`, p.stats.turns[get(state).turn])
                /*if(p.stats.turns[get(state).turn].attack){
                    logText(`${p.captain} used [[attack]] effects this turn, so ${cc(c)} will be logged`);
                    await doAction(p, 'log', {obj: c.uniqueID});
                }*/
                if(p.decks.play.some(c => matches(c, 'attack'))){
                    logText(`[[attack]] effects in play area, so ${cc(c)} will be logged`);
                    await doAction(p, 'log', {obj: c.uniqueID});
                }
            }else if(e == 'needs_away'){
                console.log(`Checking whether ${c.name} has Away Teams (if not it will be dismissed)`);
                if(myAway(p, c) == 0 && theirAway(p, c) == 0){
                    logText(`No away teams on ${cc(c)} at start of Clean-up Step, so ${cc(c)} will be dismissed`);
                    await doAction(p, 'dismiss', {obj: c.uniqueID});
                }
            }else if(e == 'unspent_check'){
                console.log('Checking unspent actions');
                let action = ongoingEffects(c, 'unspent_action');
                let numberOfTimes = p.actions;
                openWorkingLog();
                logText(`Checking unspent actions: ${p.actions} actions left`);
                for(let i = 0; i < numberOfTimes; i++){
                    //await resolveAction(p, getCard(params.card), get(state).cache.prevAction.a, get(state).cache.prevAction.attack, get(state).cache.prevAction.forced);
                    for(let e of [action].flat(2)){
                        await resolveAction(p, c, e, e.attack);
                    }
                }
                closeWorkingLog();
            }
        }
        
    }
    for(let c of [...allScoreableCards(p), ...(p.dashboard.passives || [])]){
        if(c.temporaryTraits){
            for(let t of c.temporaryTraits){
                if(c.traits.includes(t)){
                    c.traits = [...c.traits.slice(0, c.traits.indexOf(t)), ...c.traits.slice(c.traits.indexOf(t) + 1)];
                }
            }
            delete c.temporaryTraits;
        }
    }
}

export async function claimLocation(p, c){
    if(!canGainControl(c)) throw 'uncompleted';
    logText(`claimed location ${c.name}`);
    let cancelSave = clone(get(state));
    let tokensSecuring = 0;
    if(myAway(p, c, 'remove_away')){
        tokensSecuring += myAway(p, c, 'remove_away');
        try{
            await doAction(p, 'remove_away', {card: c.uniqueID, obj: {num: 'all'}, takingControl: true}); // myAway(p, c)
        }catch(e){
            if(e == 'cancelled'){
                state.update(s => {
                    return cancelSave;
                })
                return;
            }
            if(!['completed', 'uncompleted'].includes(e)) throw e;
        }
    }
    /*if(p.captain == 'TosKirk' && c.beamed){
        for(let b of c.beamed){
            try{
                await doAction(p, 'dismiss', {obj: b.uniqueID, takingControl: true});
            }catch(e){
                if(e == 'cancelled'){
                    state.update(s => {
                        return cancelSave;
                    })
                    return;
                }
                if(!['completed', 'uncompleted'].includes(e)) throw e;
            }
        }
    }*/
    if(myOrbiting(p, c)){
        let ships = c.orbiting.filter(o => o.sourcePlayer == getPlayerIndex(p));
        tokensSecuring += ships.length;
        for(let s of ships){
           // await doAction(p, 'dismiss_ship', {card: c.uniqueID, ship: s.ship});
           try{
            await doAction(p, 'dismiss', {obj: s.card, takingControl: true});
           }catch(e){
                if(e == 'cancelled'){
                    state.update(s => {
                        return cancelSave;
                    })
                    return;
                }
                if(!['completed', 'uncompleted'].includes(e)) throw e;
           }
        }
    }
    await doActionPause();
    if(c.murf){
        // murf owner gets a glory just for Murf
        let murfOwner = get(state).players.find(p => hasMurf(p));
        if(murfOwner){
            logText(`Murf owner gains 1 [[glory]] for Murf's valiant exploits on ${cc(c)}!`);
            await doAction(murfOwner, 'gain_tokens', {token: 'glory', amount: 1})
        }
        
    }
    for(let o of get(state).players.filter(pl => pl != p)){
        let gloryToGain = countPlayerTokens(c, o); //myAway(o, c) + myOrbiting(o, c);
        if(gloryToGain == 0) continue;
        if(myAway(o, c)) await doAction(o, 'remove_away', {card: c.uniqueID, takingControl: true, obj: {num: 'all'}});
        if(drones(o, c)) await doAction(o, 'remove_drones', {obj: {num: 'all'}, card: c.uniqueID});
        if(o.captain == 'TosKirk' && c.beamed){
            for(let b of c.beamed){
                try{
                    await doAction(o, 'dismiss', {obj: b.uniqueID, takingControl: true});
                }catch(e){
                    if(e == 'cancelled'){
                        state.update(s => {
                            return cancelSave;
                        })
                        return;
                    }
                    if(!['completed', 'uncompleted'].includes(e)) throw e;
                }
            }
        }
        if(myOrbiting(o, c)){
            let ships = c.orbiting.filter(orb => orb.sourcePlayer == getPlayerIndex(o));
            for(let s of ships){
                try{
                    await doAction(p, 'dismiss', {obj: s.card, takingControl: true});
                }catch(e){
                    if(e == 'cancelled'){
                        state.update(s => {
                            return cancelSave;
                        })
                        return;
                    }
                    if(!['completed', 'uncompleted'].includes(e)) throw e;
                }
                
                //await doAction(o, 'dismiss_ship', {card: c.uniqueID, ship: s.ship, takingControl: true});
            }
        }
        logText(`${o.captain} gains ${gloryToGain} [[glory]] for losing location`);
        await doAction(o, 'gain_tokens', {token: 'glory', amount: gloryToGain })
    }
    await doActionPause();
    /*if(battle){
        logText(`Battle was fought on ${c.name}, so winner forced to take incident`);
        await doAction(p, 'take', {obj: {types: 'incident'}});
    }*/
    state.update(s => { // !!! TODO check if this works/lasts
        if(!s.cache) s.cache = {};
        if(!s.cache.claimedLocations) s.cache.claimedLocations = [];
        s.cache.claimedLocations.push(c.uniqueID);
        if(!s.cache.tokensToClaim) s.cache.tokensToClaim = {};
        s.cache.tokensToClaim[c.uniqueID] = tokensSecuring;
        return s;
    })
    await doAction(p, 'control', {card: c.uniqueID, fromClaim: true});
    await doActionPause();
}

export function canBurn(){
    if(get(res).BvB && !get(res).bvbBurn) return false;
    for(let p of get(state).players){
        if(p.captain == 'Q' && p.bot && cardsInPlay(p).some(c => c.suits.includes('encounter'))) return false;
        if(ongoingPlayer(p).includes('cannot_burn')) return false;
    }
    return true;
}

export function endGame(reason){

    if(reason == 'Burn' && !canBurn()) return;

    state.update(s => {
        if(reason == 'Burn'){
            s.gameEndTrigger = reason;
            s.ended = true;
            s.burn = true;
            s.endNow = true;
            s.gameEndTurn = s.turn;
        }else if(reason == 'Immediate scoring'){
            s.gameEndTrigger = reason;
            s.burn = false;
            s.gameEndTurn = s.turn;
            s.endNow = true;
        }else if(!s.gameEndTrigger){
            s.gameEndTurn = s.turn + 1;
            s.gameEndTrigger = reason;
        }
        return s;
    })
    //if(reason == 'Burn') throw 'burn';
    //updateState(); // Should do elsewhere
}
